import React from "react";
import en from "./assets/locales/en.json";
import fr from "./assets/locales/fr.json";

export type Locale = "en" | "fr";

type TranslationPrimitive = string | number | boolean | null;
type TranslationValue =
  | TranslationPrimitive
  | TranslationValue[]
  | { [key: string]: TranslationValue };

type TranslationDictionary = Record<string, TranslationValue>;

type TranslationContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  t: (key: string, variables?: Record<string, string | number>) => string;
  raw: <T = TranslationValue>(key: string) => T;
};

const STORAGE_KEY = "delyth_locale";
const DEFAULT_LOCALE: Locale = "en";
const SUPPORTED_LOCALES: Locale[] = ["en", "fr"];

const resources: Record<Locale, TranslationDictionary> = {
  en,
  fr,
};

const TranslationContext = React.createContext<TranslationContextValue | null>(
  null
);

const isSupportedLocale = (value: string | null | undefined): value is Locale =>
  Boolean(value) && SUPPORTED_LOCALES.includes(value as Locale);

const getNestedTranslation = (
  locale: Locale,
  key: string
): TranslationValue | undefined => {
  const segments = key.split(".");
  let current: TranslationValue | undefined = resources[locale];

  for (const segment of segments) {
    if (
      typeof current !== "object" ||
      current === null ||
      Array.isArray(current) ||
      !(segment in current)
    ) {
      return undefined;
    }

    current = current[segment];
  }

  return current;
};

const interpolate = (
  template: string,
  variables?: Record<string, string | number>
) => {
  if (!variables) {
    return template;
  }

  return template.replace(/\{\{(\w+)\}\}/g, (_, token: string) => {
    const value = variables[token];
    return value === undefined ? `{{${token}}}` : String(value);
  });
};

export const detectPreferredLocale = (): Locale => {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  const storedLocale = window.localStorage.getItem(STORAGE_KEY);
  if (isSupportedLocale(storedLocale)) {
    return storedLocale;
  }

  const browserLocales = [
    ...(window.navigator.languages || []),
    window.navigator.language,
  ];

  for (const browserLocale of browserLocales) {
    const candidate = browserLocale?.toLowerCase().split("-")[0];
    if (isSupportedLocale(candidate)) {
      return candidate;
    }
  }

  return DEFAULT_LOCALE;
};

export const getTranslationValue = <T = TranslationValue,>(
  locale: Locale,
  key: string
): T => {
  const localeValue = getNestedTranslation(locale, key);
  const fallbackValue = getNestedTranslation(DEFAULT_LOCALE, key);
  const value = localeValue ?? fallbackValue;

  if (value === undefined) {
    return key as T;
  }

  return value as T;
};

export const translate = (
  locale: Locale,
  key: string,
  variables?: Record<string, string | number>
) => {
  const value = getTranslationValue<TranslationValue>(locale, key);
  if (typeof value !== "string") {
    return key;
  }

  return interpolate(value, variables);
};

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [locale, setLocale] = React.useState<Locale>(() => detectPreferredLocale());

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, locale);
    }

    document.documentElement.lang = locale;
  }, [locale]);

  const value = React.useMemo<TranslationContextValue>(
    () => ({
      locale,
      setLocale,
      toggleLocale: () => setLocale((current) => (current === "en" ? "fr" : "en")),
      t: (key, variables) => translate(locale, key, variables),
      raw: (key) => getTranslationValue(locale, key),
    }),
    [locale]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = React.useContext(TranslationContext);

  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }

  return context;
};
